import React, { useEffect, useState } from 'react';
import { apiService } from 'services/base.service';
import MetadataDropdown from 'components/metadata/metadata-dropdown';
import useBrandContext from 'contexts/brand.context';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { SuccessModal } from 'components/base/success-modal';
import SingleBottomNav from 'components/base/single-bottom-nav';
import useDashboardContext from 'contexts/dashboard.context';
import {
  BrandMetadataResponse,
  CreateMetadataResponse,
  MetadataItemType
} from 'pages/metadata/types';
import { MetadataType } from 'pages/create-brand/create-brand-metadata/types';

const CREATE_ENDPOINT = {
  brand: 'marketing/create-brand-keyword',
  descriptive: 'marketing/create-brand-descriptive-metadata',
  business: 'marketing/create-brand-business-metadata'
};

const EditBrandMetadata = () => {
  const { activeBrand, createBrandForm, setCreateBrandForm } = useBrandContext();
  const [keywordsList, setKeywordsList] = useState<MetadataType[]>([]);
  const [descriptiveMetadataList, setDescriptiveMetadataList] = useState<MetadataType[]>([]);
  const [businessMetadataList, setBusinessMetadataList] = useState<MetadataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { category } = useDashboardContext();

  const MAP_LOCAL_STATE = {
    brand: setKeywordsList,
    descriptive: setDescriptiveMetadataList,
    business: setBusinessMetadataList
  };

  const [selectedKeywords, setSelectedKeywords] = useState<MetadataItemType[]>(
    createBrandForm.brand_keywords
  );
  const [selectedDescriptiveMetadata, setSelectedDescriptiveMetadata] = useState<
    MetadataItemType[]
  >(createBrandForm.brand_descriptive_metadatas);
  const [selectedBusinessMetadata, setSelectedBusinessMetadata] = useState<MetadataItemType[]>(
    createBrandForm.brand_business_metadatas
  );

  const getKeywordList = async () => {
    try {
      const res = await apiService.post('marketing/keywords-new', {
        data: {
          keyword_type: 'brand_metadata',
          item_type: category?.toLocaleUpperCase()
        }
      });
      const data = res.data.data as BrandMetadataResponse;
      setKeywordsList(data.brand_keyword);
      setDescriptiveMetadataList(data.brand_descriptive_metadata);
      setBusinessMetadataList(data.brand_business_metadata);
    } catch {
      setKeywordsList([]);
      setDescriptiveMetadataList([]);
      setBusinessMetadataList([]);
    }
  };

  useEffect(() => {
    getKeywordList();
  }, []);

  const onKeywordTagClick = (item: MetadataItemType) => {
    const exists = !!selectedKeywords.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedKeywords([...selectedKeywords, item]);
    } else {
      setSelectedKeywords((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onDescriptiveTagClick = (item: MetadataItemType) => {
    const exists = !!selectedDescriptiveMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedDescriptiveMetadata([...selectedDescriptiveMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const onBusinessTagClick = (item: MetadataItemType) => {
    const exists = !!selectedBusinessMetadata.find((k) => k.id === item.id);
    if (!exists) {
      setSelectedBusinessMetadata([...selectedBusinessMetadata, item]);
    } else {
      setSelectedBusinessMetadata((words) => words.filter((w) => w.id !== item.id));
    }
  };
  const createMetadata = async (name: string, type: keyof typeof CREATE_ENDPOINT) => {
    const API_URL = CREATE_ENDPOINT[type];
    try {
      const res = await apiService.post(API_URL, {
        data: {
          name,
          item_type: category?.toUpperCase()
        }
      });
      const { message, id } = res.data as CreateMetadataResponse;
      toast(<ToastMessage type='success' message={message} />);
      MAP_LOCAL_STATE[type]((p) => [{ name, id }, ...p]);
    } catch {
      toast(<ToastMessage type='error' message='There was a problem adding metadata' />);
    }
  };

  useEffect(() => {
    setCreateBrandForm({
      ...createBrandForm,
      brand_keywords: selectedKeywords,
      brand_descriptive_metadatas: selectedDescriptiveMetadata,
      brand_business_metadatas: selectedBusinessMetadata
    });
  }, [selectedKeywords, selectedDescriptiveMetadata, selectedBusinessMetadata]);
  const updateBrandDetails = async () => {
    setLoading(true);
    const brand_keywords = createBrandForm.brand_keywords.map((metadata) => {
      return {
        id: metadata.id,
        name: metadata.name
      };
    });
    const brand_descriptive_metadatas = createBrandForm.brand_descriptive_metadatas.map(
      (metadata) => {
        return {
          id: metadata.id,
          name: metadata.name
        };
      }
    );
    const brand_business_metadatas = createBrandForm.brand_business_metadatas?.map((metadata) => {
      return {
        id: metadata.id,
        name: metadata.name
      };
    });
    const payload = {
      brand_id: activeBrand?.id,
      ...(brand_business_metadatas.length && { brand_business_metadatas }),
      ...(brand_descriptive_metadatas.length && { brand_descriptive_metadatas }),
      ...(brand_keywords.length && { brand_keywords })
    };
    try {
      const res = await apiService.put('marketing/brand', { data: payload });
      if (res?.data) {
        setShowSuccess(true);
      }
    } catch {
      toast(<ToastMessage type='error' message='There was a problem updating brand' />);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <MetadataDropdown
        title='Keywords'
        description='Select keyword'
        metadataList={keywordsList}
        selectedMetadata={selectedKeywords}
        onCreate={(w) => createMetadata(w, 'brand')}
        onTagClick={onKeywordTagClick}
      />
      <MetadataDropdown
        title='Descriptive Metadata'
        description='Select descriptive metadata'
        metadataList={descriptiveMetadataList}
        selectedMetadata={selectedDescriptiveMetadata}
        onCreate={(w) => createMetadata(w, 'descriptive')}
        onTagClick={onDescriptiveTagClick}
      />
      <MetadataDropdown
        title='Business Metadata'
        description='Select Business metadata'
        metadataList={businessMetadataList}
        selectedMetadata={selectedBusinessMetadata}
        onCreate={(w) => createMetadata(w, 'business')}
        onTagClick={onBusinessTagClick}
      />
      <SingleBottomNav loading={loading} onConfirm={updateBrandDetails} />
      <SuccessModal onClose={() => setShowSuccess(false)} show={showSuccess}>
        Brand updated successfully
      </SuccessModal>
    </div>
  );
};

export default EditBrandMetadata;
